import React, { FC, useRef, useContext, useState, useEffect } from "react";
import { useInView, useInViewForListRef } from "../../hooks/useInView";
import "./pressListItem.scss";
import Button from "../../components/Button";
import { t } from "i18next";
import { getCurrentLanguage } from "../../i18n";
import Image from "../Image";
import { PortableText } from "@portabletext/react";
import NavContext from "../Layout/Nav/NavProvider";
import { FilterUrlConstants, getSectionSlug, YouplanetSections } from '../../constants/general';

interface PressListItemProps {
  item: any;
  isDetailMode?: boolean;
  hasSeoTitle?: boolean;
  itemId?: number;
}
const PressListItem: FC<PressListItemProps> = ({
  item,
  isDetailMode,
  hasSeoTitle,
  itemId
}) => {
  const { isMobile } = useContext(NavContext);
  const [isMobileActive, setIsMobileActive] = useState<boolean>(false);

  const itemIndex = itemId || 0;
  const refItems = useRef([]);
  const defaultRef = useRef<HTMLDivElement>(); 

  const inView = isDetailMode ?
  useInView(defaultRef, {
    root: null,
    rootMargin: '0px',
    threshold: 0,
  }, true)    
   :
   useInViewForListRef(refItems, {
    root: null,
    rootMargin: '0px',
    threshold: 0.05,
  }, itemIndex, true);

    
  useEffect(() => {
    setIsMobileActive(isMobile());
  }, [isMobile])

  const currentLanguage = getCurrentLanguage();

  function getDateFormatted(date: Date): string {
    const parsedDate = new Date(date);
    return parsedDate
      .toLocaleString(currentLanguage, { month: "short", year: "numeric" })
      .split(" ")
      .join(", ");
  }

  function contentToPlainText(blocks = []) {
    return blocks
      .map((block) => {
        if (block._type !== "block" || !block.children) {
          return "";
        }
        return block.children.map((child) => child.text).join("");
      })
      .join("\n\n");
  }

  function getShortDescription(description: any[]) {
    const splitCount = 25;
    const splitedText = contentToPlainText(description).split(" ");

    if (splitedText.length < splitCount || isDetailMode) {
      return description;
    }

    return splitedText.slice(0, splitCount).join(" ") + "...";
  }

  function pressImage() {
    return (
      <Image
        image={item.image}
        title={item.name}
        className={`press-list-item__image ${
          isDetailMode ? "press-list-item__image--detail" : ""
        }`}
      />
    );
  }

  function buildPressLink(tagValue: string) {
    const params = `?${FilterUrlConstants.Category}=${tagValue}`;

    const url = getSectionSlug(YouplanetSections.PressList, getCurrentLanguage(), params );
    return url;
  }

  return (
    <div
      ref={el => {
        
        if (isDetailMode && defaultRef) {
          return defaultRef.current = el;
        }
        return refItems.current[itemIndex] = el;
      }}
      className={`press-list-item ${isMobileActive && inView ? "is-active" : ""} ${isMobileActive && itemIndex === 1 ? 'is-delayed' :''} ${
        isDetailMode ? "is-detail" : ""
      }
      ${isMobileActive ? "is-mobile" : ""}`}
    >
      <div className="press-list-item__content">
        {!isMobileActive && <div className="press-list-item__separator" />}
        <div
          className={`press-list-item__section ${
            isDetailMode ? "press-list-item__section--detail" : ""
          }`}
        >
          <div className="press-list-item__col">
            <div
              className={`press-list-item__image-wrapper ${
                isDetailMode ? "press-list-item__image-wrapper--no-space" : ""
              }`}
            >
              {!!item.image ? (
                isDetailMode ? (
                  pressImage()
                ) : (
                  <a href={`/${item.slug.current}`}>{pressImage()}</a>
                )
              ) : (
                <div className="press-list-item__image press-list-item__image--empty">
                  {t("common.noImage")}
                </div>
              )}
            </div>
            <div
              className={`press-list-item__tags ${
                !isDetailMode ? "is-hidden" : ""
              }`}
            >
              {isDetailMode &&
                item.tags.map((tag) => (
                  <div
                    key={`tag-${tag.name}`}
                    className="press-list-item__button-space"
                  >
                    <Button uppercase isLink href={buildPressLink(tag.name)} >
                      {tag.name}
                    </Button>
                  </div>
                ))}
            </div>
          </div>
          <div className="press-list-item__col press-list-item__col--no-space">
            {isMobileActive && <div className="press-list-item__separator" />}
            <span className="press-list-item__date">
              {getDateFormatted(item.date)}
            </span>
            {hasSeoTitle ? (
              <h1 className="press-list-item__title">{item.name}</h1>
            ) : (
              <h2 className="press-list-item__title">{item.name}</h2>
            )}
            {!isMobileActive && (
              <div
                className={`press-list-item__button ${
                  isDetailMode ? "is-hidden" : ""
                }`}
              >
                <Button uppercase solid isLink href={`/${item.slug.current}`}>
                  {t("common.readMore")} →
                </Button>
              </div>
            )}
            <div className="press-list-item__row">
              <div
                className={`press-list-item__description ${
                  isDetailMode
                    ? "press-list-item__description--detail scrollable custom-scrollbar cursor-click"
                    : "is-hidden"
                }`}
                data-cursor-text="Scroll"
                data-cursor-text-color="#fff"
              >
                {isDetailMode ? (
                  <PortableText value={item.content} />
                ) : (
                  getShortDescription(item.content)
                )}
              </div>
            </div>
            {isMobileActive && (
              <div
                className={`press-list-item__button ${
                  isDetailMode ? "is-hidden" : ""
                }`}
              >
                <Button uppercase solid isLink href={`/${item.slug.current}`}>
                  {t("common.readMore")} →
                </Button>
              </div>
            )}
            {isMobileActive && (
              <div className="press-list-item__separator press-list-item__separator--bottom"></div>
            )}
          </div>
        </div>
        {!isMobileActive && (
          <div className="press-list-item__separator press-list-item__separator--bottom"></div>
        )}
      </div>
    </div>
  );
};

export default PressListItem;
